import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { submitAccessRequest } from "@/lib/auth/auth.functions";
import { subscribeEmail } from "@/lib/marketing/subscribers.functions";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ArrowRight, CheckCircle2, Shield, Zap, Lock, Brain } from "lucide-react";

export const Route = createFileRoute("/get-software")({
  head: () => ({
    meta: [
      { title: "Request CARIO INTEL — Get the software" },
      {
        name: "description",
        content:
          "Get access to CARIO. Search hundreds of public sources at once and keep a sourced case file for every check you run.",
      },
      { property: "og:title", content: "Request CARIO INTEL" },
      {
        property: "og:description",
        content: "Find out who you're actually dealing with. Request access.",
      },
    ],
  }),
  component: GetSoftwarePage,
});

function GetSoftwarePage() {
  const submit = useServerFn(submitAccessRequest);
  const subscribe = useServerFn(subscribeEmail);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await submit({
        data: { ...form, organization: form.organization || null },
      });
      // Also add them to the list so admin sees the email even before approval.
      try {
        await subscribe({
          data: {
            email: form.email,
            source: "get-software",
            user_agent:
              typeof navigator !== "undefined"
                ? navigator.userAgent.slice(0, 480)
                : null,
          },
        });
      } catch {
        /* non-blocking */
      }
      setDone(true);
    } catch (e: any) {
      toast.error(e?.message || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster />
      <header className="sticky top-0 z-40 border-b border-panel-border bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6">
          <Link to="/"><Logo /></Link>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 sm:py-20 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-panel-border bg-panel/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan pulse-dot" />
              Restricted environment · vetted operators only
            </div>
            <h1 className="mt-6 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
              Request the <span className="text-cyan text-glow">CARIO INTEL</span> platform.
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground">
              CARIO INTEL is not self-serve. Tell us who you are and what you investigate.
              Approved operators receive an invitation link to provision credentials.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                { i: Brain, t: "AI correlation, contradiction detection, anomaly scoring" },
                { i: Shield, t: "30+ collectors · WHOIS · DNS · sanctions · breach · on-chain" },
                { i: Lock, t: "Audit trail, integrity hashes and exportable PDF reports" },
                { i: Zap, t: "Force-directed entity graphs · live telemetry" },
              ].map(({ i: I, t }) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <I className="h-4 w-4 text-cyan mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <div className="glass-strong rounded-xl p-6 sm:p-8 ring-glow">
              {done ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
                  <h2 className="mt-4 font-display text-2xl">Request received</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    An administrator will review your request and issue an invitation if approved.
                  </p>
                  <Link
                    to="/"
                    className="mt-6 inline-flex text-cyan hover:underline text-sm"
                  >
                    ← Back to home
                  </Link>
                </div>
              ) : (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
                    // Software access
                  </p>
                  <h2 className="mt-2 font-display text-2xl">Tell us about you</h2>
                  <form onSubmit={onSubmit} className="mt-6 space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="full_name">Full name</Label>
                      <Input
                        id="full_name"
                        required
                        maxLength={120}
                        value={form.full_name}
                        onChange={(e) =>
                          setForm({ ...form, full_name: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Work email</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        maxLength={255}
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="organization">Organization</Label>
                      <Input
                        id="organization"
                        maxLength={160}
                        value={form.organization}
                        onChange={(e) =>
                          setForm({ ...form, organization: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="reason">What will you investigate?</Label>
                      <Textarea
                        id="reason"
                        required
                        minLength={10}
                        maxLength={2000}
                        rows={5}
                        value={form.reason}
                        onChange={(e) =>
                          setForm({ ...form, reason: e.target.value })
                        }
                        placeholder="Describe your role, mandate, and the kind of casework you'll run through the platform."
                      />
                    </div>
                    <Button type="submit" disabled={busy} className="w-full">
                      {busy ? "Submitting…" : (
                        <>Request software access <ArrowRight className="h-4 w-4 ml-1" /></>
                      )}
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center">
                      By submitting, you join our briefing list. Unsubscribe anytime.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}